import { useMutation } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { track } from '@core/logging';
import { useCallback } from 'react';

import { DELETE_AUDIT_SESSION } from '../graphql/mutations/deleteAuditSession';

interface DeleteAuditSessionInput {
  sessionId: string;
}

interface DeleteAuditSessionVariables {
  input: DeleteAuditSessionInput;
}

interface DeleteAuditSessionResponse {
  deleteAuditSession: boolean;
}

/**
 * Hook for deleting audit sessions
 * Simplified to handle the two clear cases:
 * - Success: result.data.deleteAuditSession === true
 * - Error: result.errors exists, process first error
 * Includes strategic business event tracking for observability compliance
 */
/* eslint-disable complexity */
export function useDeleteAuditSession() {
  const [deleteAuditSessionMutation, { loading, error }] = useMutation<DeleteAuditSessionResponse, DeleteAuditSessionVariables>(DELETE_AUDIT_SESSION, {
    errorPolicy: 'all',
  });

  const deleteAuditSession = useCallback(
    async (sessionId: string, sessionName?: string): Promise<{ success: boolean; errors?: unknown[] }> => {
      // Track audit session deletion initiation
      track('DeleteAuditSessionInitiated', {
        sessionId,
        sessionName: sessionName || 'unknown',
      });

      try {
        const result = await deleteAuditSessionMutation({
          variables: {
            input: { sessionId },
          },
        });

        // Handle error case: result.errors exists
        if (result.errors && result.errors.length > 0) {
          const transformedErrors = result.errors.map(error => transformError(error));
          const firstError = transformedErrors[0];

          // Track audit session deletion failure
          track('DeleteAuditSessionFailed', {
            sessionId,
            sessionName: sessionName || 'unknown',
            errorType: 'api_error',
            errorMessage: firstError?.message || 'Unknown error',
          });

          return { success: false, errors: transformedErrors };
        }

        // Handle success case: result.data.deleteAuditSession === true
        if (result.data?.deleteAuditSession === true) {
          // Track successful audit session deletion
          track('DeleteAuditSessionSucceeded', {
            sessionId,
            sessionName: sessionName || 'unknown',
          });

          return { success: true };
        }

        // Fallback case (shouldn't happen based on your description)
        track('DeleteAuditSessionFailed', {
          sessionId,
          sessionName: sessionName || 'unknown',
          errorType: 'unexpected_response',
          errorMessage: 'Unexpected response format',
        });

        return { success: false, errors: [{ message: 'Unexpected response format', userMessage: 'Unable to delete the audit session. Please try again.' }] };
      } catch (error) {
        // Track unexpected audit session deletion failure
        track('DeleteAuditSessionFailed', {
          sessionId,
          sessionName: sessionName || 'unknown',
          errorType: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : 'Unexpected error',
        });

        throw error;
      }
    },
    [deleteAuditSessionMutation]
  );

  return {
    deleteAuditSession,
    loading,
    error,
  };
}
